import React, { useCallback, useEffect, useMemo, useState } from "react"
import axios from "axios"
import { useNavigate, useParams } from "react-router-dom"
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap"
import {
  FaArrowLeft,
  FaCheckCircle,
  FaComments,
  FaCloudUploadAlt,
  FaDownload,
  FaFileAlt,
  FaFilter,
  FaImage,
  FaPlus,
  FaPaperclip,
  FaRedo,
  FaSearch,
  FaSort,
  FaSortDown,
  FaSortUp,
  FaTicketAlt,
  FaTimes,
  FaTrash,
  FaUser,
} from "react-icons/fa"
import CompanySelector from "ATSComponents/Common/CompanySelector"
import Swal from "sweetalert2"

const MODULES = [
  "Dashboard",
  "Organization",
  "Job Orders",
  "Employees",
  "Candidates",
  "Reports",
  "Admin Module",
  "Agent Creation",
  "Telephonic Tests",
  "SMTP",
  "Other",
]
const STATUS_OPTIONS = [
  { label: "Open", value: "open" },
  { label: "In Progress", value: "in_progress" },
  { label: "Waiting on Requester", value: "waiting_on_requester" },
  { label: "Resolved", value: "resolved" },
  { label: "Closed", value: "closed" },
]
const STATUSES = STATUS_OPTIONS.map(option => option.label)
const PRIORITIES = ["Low", "Medium", "High", "Urgent"]
const RESOLUTION_STATUSES = ["completed", "pending", "not_completed"]
const STATUS_MANAGER_EMAILS = new Set([
  "anwar@pranathiss.com",
  "pssadmin@bharatpayroll.com",
])
const MAX_ATTACHMENT_SIZE = 10 * 1024 * 1024
const ALLOWED_ATTACHMENT_EXTENSIONS = [
  "png",
  "jpg",
  "jpeg",
  "pdf",
  "doc",
  "docx",
  "xls",
  "xlsx",
]
const apiErrorMessage = error => {
  const data = error?.response?.data
  if (typeof data === "string") return data
  if (data?.detail || data?.message || data?.error)
    return data.detail || data.message || data.error
  if (data && typeof data === "object")
    return (
      Object.values(data).flat().join(" ") ||
      "Unable to complete the request. Please try again."
    )
  return "Unable to complete the request. Please try again."
}
const showTicketError = message =>
  Swal.fire({
    icon: "error",
    title: "Ticket request failed",
    text: message,
    confirmButtonText: "OK",
  })

const readUser = () => {
  try {
    return JSON.parse(sessionStorage.getItem("user-info")) || {}
  } catch (error) {
    return {}
  }
}

const getList = data => {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  if (Array.isArray(data?.data)) return data.data
  if (Array.isArray(data?.message)) return data.message
  return []
}
const first = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== "")
const idOf = item => first(item?.id, item?.user_id, item?.userId, item?.pk)
const formatDate = value =>
  value
    ? new Date(value).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "-"
const normalize = value =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]/g, " ")
const getBearerToken = () => {
  try {
    const userInfo = JSON.parse(sessionStorage.getItem("user-info")) || {}
    return (
      userInfo.token ||
      userInfo.access ||
      userInfo.access_token ||
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("Admintoken") ||
      ""
    )
  } catch (error) {
    return (
      sessionStorage.getItem("token") ||
      sessionStorage.getItem("Admintoken") ||
      ""
    )
  }
}
const authConfig = extra => {
  const token = getBearerToken()
  return {
    ...extra,
    headers: {
      ...(extra?.headers || {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  }
}
const fileExtension = name =>
  String(name || "")
    .split(".")
    .pop()
    .toLowerCase()
const isImageFile = file =>
  ["png", "jpg", "jpeg"].includes(
    fileExtension(file?.name || file?.file_name || file?.filename)
  ) ||
  String(file?.content_type || file?.mime_type || file?.type || "").startsWith(
    "image/"
  )
const fileSize = bytes => {
  if (!bytes) return "0 B"
  const units = ["B", "KB", "MB", "GB"]
  const index = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  )
  return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`
}
const attachmentId = attachment =>
  first(attachment?.id, attachment?.attachment_id, attachment?.pk)
const attachmentUrl = attachment =>
  first(
    attachment?.url,
    attachment?.file_url,
    attachment?.attachment_url,
    attachment?.file,
    attachment?.path,
    ""
  )
const resolvedAttachmentUrl = attachment => {
  const value = attachmentUrl(attachment)
  if (!value || /^https?:\/\//i.test(value) || value.startsWith("blob:"))
    return value
  const base = process.env.REACT_APP_API_BASE_URL || ""
  return `${base.replace(/\/$/, "")}/${String(value).replace(/^\//, "")}`
}
const attachmentName = attachment =>
  first(
    attachment?.name,
    attachment?.file_name,
    attachment?.filename,
    String(attachmentUrl(attachment))
      .split("?")[0]
      .split("#")[0]
      .split("/")
      .pop(),
    "Attachment"
  )
const ticketAttachments = ticket =>
  Array.isArray(ticket?.attachments)
    ? ticket.attachments
    : Array.isArray(ticket?.ticket_attachments)
    ? ticket.ticket_attachments
    : Array.isArray(ticket?.files)
    ? ticket.files
    : []
const ticketFeedbackStatus = ticket =>
  normalize(
    first(
      ticket?.resolution_feedback_status,
      ticket?.feedback_status,
      ticket?.resolution_status,
      ticket?.feedback?.status,
      ""
    )
  ).replace(/ /g, "_")
const ticketFeedbackText = ticket =>
  first(
    ticket?.resolution_feedback,
    ticket?.feedback_text,
    ticket?.feedback?.feedback,
    ticket?.feedback?.text,
    ""
  )
const ticketFeedbackAuthor = ticket =>
  first(
    ticket?.feedback_author_name,
    ticket?.resolution_feedback_author,
    ticket?.feedback?.author_name,
    ticket?.feedback?.author?.name,
    ""
  )
const ticketFeedbackTimestamp = ticket =>
  first(
    ticket?.feedback_timestamp,
    ticket?.resolution_feedback_at,
    ticket?.feedback?.created_at,
    ""
  )
const ticketRequester = ticket => first(ticket?.requester_name, "Unknown")
const ticketCreatedBy = ticket =>
  first(
    ticket?.created_by_name,
    ticket?.created_by_email,
    ticket?.created_by?.email,
    "Unknown"
  )
const ticketCommentCount = ticket =>
  first(
    ticket?.comment_count,
    ticket?.comments_count,
    Array.isArray(ticket?.comments) ? ticket.comments.length : 0
  )
const resolutionLabel = status =>
  status === "not_completed"
    ? "Not Completed"
    : status
    ? status.charAt(0).toUpperCase() + status.slice(1)
    : ""
const ticketStatusValue = ticket =>
  normalize(first(ticket?.status, "open")).replace(/ /g, "_")
const ticketStatusLabel = status =>
  STATUS_OPTIONS.find(option => option.value === ticketStatusValue({ status }))
    ?.label || first(status, "Open")

const TicketManagement = () => {
  const navigate = useNavigate()
  const { ticketId } = useParams()
  const userInfo = readUser()
  const user = userInfo?.user_details || userInfo
  const role = String(
    first(user?.role_name, user?.role, user?.user_type, "")
  ).toLowerCase()
  const isAdmin = Boolean(
    user?.is_superuser ||
      user?.is_staff ||
      userInfo?.is_superuser ||
      role.includes("admin") ||
      role.includes("superuser")
  )
  const isSuperAdmin = Boolean(
    user?.is_superuser ||
      userInfo?.is_superuser ||
      role.replace(/\s/g, "").includes("superadmin")
  )
  const loggedInEmail = String(
    first(
      user?.email,
      user?.user_email,
      user?.login_email,
      userInfo?.email,
      userInfo?.user_email,
      ""
    )
  )
    .trim()
    .toLowerCase()
  const canManageStatus = STATUS_MANAGER_EMAILS.has(loggedInEmail)
  const defaultCompany =
    user?.companies_data?.[0]?.company_id ||
    sessionStorage.getItem("active-company-id") ||
    ""

  const [companyId, setCompanyId] = useState(defaultCompany)
  const [tickets, setTickets] = useState([])
  const [ticketCount, setTicketCount] = useState(0)
  const [selectedTicket, setSelectedTicket] = useState(null)
  const [comments, setComments] = useState([])
  const [loading, setLoading] = useState(true)
  const [detailLoading, setDetailLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [resolutionChoice, setResolutionChoice] = useState("")
  const [resolutionFeedback, setResolutionFeedback] = useState("")
  const [selectedFiles, setSelectedFiles] = useState([])
  const [dragActive, setDragActive] = useState(false)
  const [previewAttachment, setPreviewAttachment] = useState(null)
  const [commentText, setCommentText] = useState("")
  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState({
    module: "",
    subject: "",
    description: "",
    priority: "Medium",
  })
  const [filters, setFilters] = useState({
    date_from: "",
    date_to: "",
    status: "",
    priority: "",
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" })

  const openTicket = ticket => {
    const id = idOf(ticket)
    if (!id) return
    setSelectedTicket(ticket)
    navigate(`/tickets/${id}`)
  }

  const closeTicket = () => {
    setSelectedTicket(null)
    navigate("/tickets")
  }

  const fetchTickets = useCallback(async () => {
    if (!companyId) {
      setTickets([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const params = { company_id: companyId }
      Object.entries(appliedFilters).forEach(
        ([key, value]) => value && (params[key] = value)
      )
      const response = await axios.get("/api/v1/tickets/", {
        params,
        timeout: 15000,
        ...authConfig(),
      })
      setTickets(getList(response.data))
      setTicketCount(response.data?.count || getList(response.data).length)
    } catch (err) {
      showTicketError(apiErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }, [companyId, appliedFilters, isAdmin])

  const fetchComments = useCallback(async ticket => {
    if (!ticket) return
    setDetailLoading(true)
    try {
      const response = await axios.get("/api/v1/ticket-comments/", {
        params: { ticket_id: idOf(ticket) },
        timeout: 15000,
        ...authConfig(),
      })
      setComments(getList(response.data))
    } catch (err) {
      showTicketError(apiErrorMessage(err))
    } finally {
      setDetailLoading(false)
    }
  }, [])

  const fetchTicketDetail = useCallback(async ticket => {
    if (!ticket || !idOf(ticket)) return
    setDetailLoading(true)
    try {
      const response = await axios.get(
        `/api/v1/tickets/${idOf(ticket)}/`,
        authConfig({ timeout: 15000 })
      )
      setSelectedTicket(response.data?.data || response.data)
    } catch (err) {
      showTicketError(apiErrorMessage(err))
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])
  useEffect(() => {
    fetchTickets()
  }, [fetchTickets])
  useEffect(() => {
    fetchComments(selectedTicket)
  }, [selectedTicket, fetchComments])
  useEffect(() => {
    setResolutionChoice("")
    setResolutionFeedback("")
  }, [selectedTicket ? idOf(selectedTicket) : null])

  useEffect(() => {
    if (!ticketId) fetchTicketDetail(selectedTicket)
  }, [ticketId, selectedTicket ? idOf(selectedTicket) : null, fetchTicketDetail])
  useEffect(() => {
    if (ticketId && !selectedTicket) {
      fetchTicketDetail({ id: ticketId })
    }
  }, [ticketId, selectedTicket, fetchTicketDetail])

  const counts = useMemo(
    () =>
      STATUSES.reduce(
        (result, status) => {
          result[status] = tickets.filter(
            ticket => normalize(ticket.status) === normalize(status)
          ).length
          return result
        },
        { All: tickets.length }
      ),
    [tickets]
  )
  // The API already scopes employee results to the logged-in employee.
  // Do not filter again here because serializers may return creator objects.
  const visibleTickets = tickets

  const sortTickets = key => {
    setSortConfig(current => ({
      key,
      direction:
        current.key === key && current.direction === "asc" ? "desc" : "asc",
    }))
  }

  const sortableValue = (ticket, key) => {
    if (key === "attachments") return ticketAttachments(ticket).length
    if (key === "comments") return ticketCommentCount(ticket)
    if (key === "created_by") return ticketCreatedBy(ticket)
    if (key === "resolution_feedback") {
      return resolutionLabel(ticketFeedbackStatus(ticket))
    }
    return first(ticket[key], "")
  }

  const sortedTickets = useMemo(() => {
    if (!sortConfig.key) return visibleTickets
    return [...visibleTickets].sort((left, right) => {
      const leftValue = sortableValue(left, sortConfig.key)
      const rightValue = sortableValue(right, sortConfig.key)
      const leftDate = Date.parse(leftValue)
      const rightDate = Date.parse(rightValue)
      const comparison =
        !Number.isNaN(leftDate) && !Number.isNaN(rightDate)
          ? leftDate - rightDate
          : String(leftValue).localeCompare(String(rightValue), undefined, {
              numeric: true,
              sensitivity: "base",
            })
      return sortConfig.direction === "asc" ? comparison : -comparison
    })
  }, [visibleTickets, sortConfig])

  const sortIcon = key => {
    if (sortConfig.key !== key) return <FaSort />
    return sortConfig.direction === "asc" ? <FaSortUp /> : <FaSortDown />
  }

  const updateTicketFilter = (key, value) => {
    const nextFilters = { ...filters, [key]: value }
    setFilters(nextFilters)
    setAppliedFilters(nextFilters)
  }

  const clearTicketFilters = () => {
    const clearedFilters = Object.keys(filters).reduce(
      (result, key) => ({ ...result, [key]: "" }),
      {}
    )
    setFilters(clearedFilters)
    setAppliedFilters(clearedFilters)
  }

  const refreshAfter = async callback => {
    setSaving(true)
    try {
      await callback()
      await fetchTickets()
      return true
    } catch (err) {
      showTicketError(apiErrorMessage(err))
      return false
    } finally {
      setSaving(false)
    }
  }
  const addFiles = files => {
    const incoming = Array.from(files || [])
    const invalid = incoming.find(file => {
      const extension = fileExtension(file.name)
      return (
        !ALLOWED_ATTACHMENT_EXTENSIONS.includes(extension) ||
        file.size > MAX_ATTACHMENT_SIZE
      )
    })
    if (invalid) {
      const message =
        invalid.size > MAX_ATTACHMENT_SIZE
          ? `${invalid.name} is larger than 10 MB.`
          : `${invalid.name} is not a supported file type.`
      Swal.fire({
        icon: "warning",
        title: "Attachment not added",
        text: message,
        confirmButtonText: "OK",
      })
      return
    }
    const existingNames = new Set(selectedFiles.map(item => item.file.name))
    const next = incoming
      .filter(file => !existingNames.has(file.name))
      .map(file => ({
        id: `${file.name}-${file.lastModified}-${Math.random()}`,
        file,
        preview: file.type.startsWith("image/")
          ? URL.createObjectURL(file)
          : "",
      }))
    setSelectedFiles(current => [...current, ...next])
  }
  const removeFile = fileId =>
    setSelectedFiles(current => {
      const removed = current.find(item => item.id === fileId)
      if (removed?.preview) URL.revokeObjectURL(removed.preview)
      return current.filter(item => item.id !== fileId)
    })
  const clearSelectedFiles = () => {
    selectedFiles.forEach(
      item => item.preview && URL.revokeObjectURL(item.preview)
    )
    setSelectedFiles([])
  }
  const closeCreateModal = () => {
    setShowCreate(false)
    clearSelectedFiles()
  }
  const createTicket = async event => {
    event.preventDefault()
    if (!form.module || !form.subject.trim() || !form.description.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing ticket details",
        text: "Module, subject, and description are required.",
        confirmButtonText: "OK",
      })
      return
    }
    const created = await refreshAfter(async () => {
      const payload = new FormData()
      payload.append("module", form.module)
      payload.append("subject", form.subject.trim())
      payload.append("description", form.description.trim())
      payload.append("priority", form.priority)
      payload.append("company_id", companyId)
      selectedFiles.forEach(item => payload.append("attachments", item.file))
      await axios.post(
        "/api/v1/tickets/",
        payload,
        authConfig({
          timeout: 120000,
        })
      )
      setShowCreate(false)
      setForm({ module: "", subject: "", description: "", priority: "Medium" })
      clearSelectedFiles()
    })
    if (created) {
      Swal.fire({
        icon: "success",
        title: "Ticket created",
        text: "Your support ticket was created successfully.",
        confirmButtonText: "OK",
      })
    }
  }
  const updateTicket = (field, value) =>
    refreshAfter(async () => {
      await axios.patch(
        `/api/v1/tickets/${idOf(selectedTicket)}/`,
        { [field]: value },
        authConfig()
      )
      const next = { ...selectedTicket, [field]: value }
      setSelectedTicket(next)
    })
  const changeStatus = async event => {
    const status = event.target.value
    setSaving(true)
    try {
      await axios.patch(
        `/api/v1/tickets/${idOf(selectedTicket)}/`,
        { status },
        authConfig()
      )
      await fetchTicketDetail(selectedTicket)
      await fetchTickets()
      Swal.fire({
        icon: "success",
        title: "Status updated",
        text: "The ticket status was updated successfully.",
        confirmButtonText: "OK",
      }).then(result => {
        if (result.isConfirmed) closeTicket()
      })
    } catch (err) {
      showTicketError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }
  const deleteAttachment = async attachment => {
    const attachmentKey = attachmentId(attachment)
    if (!attachmentKey) return
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete attachment?",
      text: "This file will be removed from the ticket.",
      showCancelButton: true,
      confirmButtonText: "Delete",
      cancelButtonText: "Cancel",
    })
    if (!result.isConfirmed) return
    await refreshAfter(async () => {
      await axios.delete(
        `/api/v1/ticket-attachments/${attachmentKey}/`,
        authConfig({ timeout: 15000 })
      )
      const response = await axios.get(
        `/api/v1/tickets/${idOf(selectedTicket)}/`,
        authConfig({ timeout: 15000 })
      )
      setSelectedTicket(response.data?.data || response.data)
    })
  }
  const addComment = async event => {
    event.preventDefault()
    if (!commentText.trim()) return
    await refreshAfter(async () => {
      await axios.post(
        "/api/v1/ticket-comments/",
        { ticket_id: idOf(selectedTicket), comment: commentText.trim() },
        authConfig()
      )
      setCommentText("")
      await fetchComments(selectedTicket)
    })
  }
  const submitResolutionFeedback = async event => {
    event.preventDefault()
    if (!resolutionChoice) {
      Swal.fire({
        icon: "warning",
        title: "Choose a resolution",
        text: "Please select Completed, Pending, or Not Completed.",
        confirmButtonText: "OK",
      })
      return
    }
    setSaving(true)
    try {
      await axios.post(
        `/api/v1/tickets/${idOf(selectedTicket)}/feedback/`,
        { status: resolutionChoice, feedback: resolutionFeedback.trim() },
        authConfig({ timeout: 15000 })
      )
      await fetchTicketDetail(selectedTicket)
      await fetchTickets()
      setResolutionChoice("")
      setResolutionFeedback("")
      Swal.fire({
        icon: "success",
        title: "Feedback submitted",
        text: "Your resolution response has been recorded.",
        confirmButtonText: "OK",
      })
    } catch (err) {
      showTicketError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const ticketNumber = ticket =>
    first(
      ticket.ticket_number,
      ticket.number,
      ticket.reference,
      `#${idOf(ticket)}`
    )
  if (selectedTicket)
    return (
      <div className="ticket-page">
        <div className="ticket-detail-header">
          <Button
            color="link"
            className="ticket-back"
            onClick={closeTicket}
          >
            <FaArrowLeft /> Back to tickets
          </Button>
          <div className="ticket-detail-heading-icon">
            <FaTicketAlt />
          </div>
          <div className="ticket-detail-heading-content">
            <div className="ticket-detail-reference-row">
              <span className="ticket-kicker">
                {ticketNumber(selectedTicket)}
              </span>
              <span className="ticket-module-pill">
                {selectedTicket.module || "Other"}
              </span>
            </div>
            <h1>{selectedTicket.subject}</h1>
            <p>
              Created{" "}
              {formatDate(
                first(selectedTicket.created_at, selectedTicket.created_date)
              )}
            </p>
          </div>
          <div className="ticket-detail-header-status">
            <span
              className={`ticket-status status-${normalize(
                selectedTicket.status
              ).replace(" ", "-")}`}
            >
              <FaCheckCircle /> {ticketStatusLabel(selectedTicket.status)}
            </span>
            <span
              className={`ticket-priority priority-${normalize(
                selectedTicket.priority
              ).replace(" ", "-")}`}
            >
              {first(selectedTicket.priority, "Medium")}
            </span>
          </div>
        </div>
        <div className="ticket-detail-grid">
          <section className="ticket-panel ticket-description">
            <div className="ticket-panel-title">
              <FaTicketAlt /> Ticket description
            </div>
            <p>{selectedTicket.description || "No description provided."}</p>
            <div className="ticket-meta-grid">
              <div>
                <small>Priority</small>
                <strong
                  className={`ticket-priority priority-${normalize(
                    selectedTicket.priority
                  ).replace(" ", "-")}`}
                >
                  {first(selectedTicket.priority, "Medium")}
                </strong>
              </div>
              <div>
                <small>Status</small>
                <strong>{ticketStatusLabel(selectedTicket.status)}</strong>
              </div>
              <div>
                <small>Requester</small>
                <strong>{ticketRequester(selectedTicket)}</strong>
              </div>
              <div>
                <small>Created By</small>
                <strong>{ticketCreatedBy(selectedTicket)}</strong>
              </div>
              <div>
                <small>Created</small>
                <strong>
                  {formatDate(
                    first(
                      selectedTicket.created_at,
                      selectedTicket.created_date
                    )
                  )}
                </strong>
              </div>
              <div>
                <small>Updated</small>
                <strong>
                  {formatDate(
                    first(
                      selectedTicket.updated_at,
                      selectedTicket.last_updated
                    )
                  )}
                </strong>
              </div>
            </div>
          </section>
          <section className="ticket-panel ticket-attachments-panel">
            <div className="ticket-panel-title">
              <FaPaperclip /> Attachments
            </div>
            {ticketAttachments(selectedTicket).length ? (
              <div className="ticket-attachment-list">
                {ticketAttachments(selectedTicket).map((attachment, index) => {
                  const url = resolvedAttachmentUrl(attachment)
                  const name = attachmentName(attachment)
                  const image = isImageFile(attachment)
                  return (
                    <div
                      className={`ticket-attachment-item ${
                        image ? "is-clickable" : ""
                      }`}
                      key={attachmentId(attachment) || index}
                      onClick={() =>
                        image && setPreviewAttachment({ url, name })
                      }
                      onKeyDown={event => {
                        if (
                          image &&
                          (event.key === "Enter" || event.key === " ")
                        ) {
                          event.preventDefault()
                          setPreviewAttachment({ url, name })
                        }
                      }}
                      role={image ? "button" : undefined}
                      tabIndex={image ? 0 : undefined}
                    >
                      {image ? (
                        <button
                          type="button"
                          className="ticket-attachment-preview"
                          onClick={() => setPreviewAttachment({ url, name })}
                          title="Preview image"
                        >
                          <img src={url} alt={name} />
                        </button>
                      ) : (
                        <div className="ticket-attachment-file-icon">
                          <FaFileAlt />
                        </div>
                      )}
                      <div className="ticket-attachment-info">
                        <strong title={name}>{name}</strong>
                        <small>
                          {first(
                            attachment.size,
                            attachment.file_size,
                            attachment.size_bytes
                          )
                            ? fileSize(
                                Number(
                                  first(
                                    attachment.size,
                                    attachment.file_size,
                                    attachment.size_bytes
                                  )
                                )
                              )
                            : String(
                                attachment.content_type ||
                                  attachment.mime_type ||
                                  fileExtension(name)
                              ).toUpperCase()}
                        </small>
                      </div>
                      {!image && (
                        <a
                          className="ticket-attachment-download"
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          download={name}
                          title="Download file"
                          onClick={event => event.stopPropagation()}
                        >
                          <FaDownload />
                        </a>
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          className="ticket-attachment-delete"
                          onClick={event => {
                            event.stopPropagation()
                            deleteAttachment(attachment)
                          }}
                          title="Delete attachment"
                        >
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            ) : (
              <div className="ticket-empty-inline">
                No attachments on this ticket.
              </div>
            )}
          </section>
          {!isSuperAdmin && (
              <section className="ticket-panel ticket-resolution-panel">
                <div className="ticket-panel-title">
                  <FaCheckCircle />
                  <span>Confirm Resolution</span>
                  <small className="ticket-resolution-owner">By Employee</small>
                </div>
                <p className="ticket-resolution-copy">Was your issue resolved?</p>
                {ticketFeedbackStatus(selectedTicket) && (
                  <div className="ticket-feedback-summary">
                    <span
                      className={`ticket-feedback-badge feedback-${ticketFeedbackStatus(
                        selectedTicket
                      )}`}
                    >
                      {resolutionLabel(ticketFeedbackStatus(selectedTicket))}
                    </span>
                    {ticketFeedbackText(selectedTicket) && (
                      <p>{ticketFeedbackText(selectedTicket)}</p>
                    )}
                    {(ticketFeedbackAuthor(selectedTicket) ||
                      ticketFeedbackTimestamp(selectedTicket)) && (
                      <small>
                        {ticketFeedbackAuthor(selectedTicket) || "User"}
                        {ticketFeedbackTimestamp(selectedTicket)
                          ? ` · ${formatDate(
                              ticketFeedbackTimestamp(selectedTicket)
                            )}`
                          : ""}
                      </small>
                    )}
                  </div>
                )}
                {ticketFeedbackStatus(selectedTicket) !== "completed" && (
                  <form onSubmit={submitResolutionFeedback}>
                    <div className="ticket-resolution-options">
                      {RESOLUTION_STATUSES.map(option => (
                        <button
                          type="button"
                          key={option}
                          className={`ticket-resolution-option ${
                            resolutionChoice === option ? "is-selected" : ""
                          }`}
                          onClick={() => setResolutionChoice(option)}
                          disabled={saving}
                        >
                          {resolutionLabel(option)}
                        </button>
                      ))}
                    </div>
                    <Input
                      type="textarea"
                      rows="3"
                      value={resolutionFeedback}
                      onChange={event =>
                        setResolutionFeedback(event.target.value)
                      }
                      placeholder="Add feedback about the resolution..."
                      disabled={saving}
                    />
                    <Button
                      type="submit"
                      color="primary"
                      disabled={saving || !resolutionChoice}
                    >
                      {saving ? "Submitting..." : "Submit response"}
                    </Button>
                  </form>
                )}
              </section>
            )}
          <section className="ticket-panel ticket-conversation-panel">
            <div className="ticket-panel-title">
              <FaComments /> Conversation
            </div>
            <div className="ticket-comments">
              {detailLoading ? (
                <div className="ticket-muted">Loading comments...</div>
              ) : comments.length ? (
                comments.map((comment, index) => (
                  <div className="ticket-comment" key={idOf(comment) || index}>
                    <div className="ticket-comment-avatar">
                      <FaUser />
                    </div>
                    <div>
                      <strong>
                        {first(
                          comment.author_name,
                          comment.created_by_name,
                          comment.user_name,
                          comment.created_by?.name,
                          "User"
                        )}
                      </strong>
                      <small>
                        {formatDate(
                          first(comment.created_at, comment.created_date)
                        )}
                      </small>
                      <p>
                        {first(
                          comment.body,
                          comment.comment,
                          comment.content,
                          comment.text,
                          ""
                        )}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="ticket-empty-inline">
                  No comments yet. Start the conversation below.
                </div>
              )}
            </div>
            <form className="ticket-comment-form" onSubmit={addComment}>
              <Input
                type="textarea"
                value={commentText}
                onChange={event => setCommentText(event.target.value)}
                placeholder="Add a comment..."
                rows="3"
              />
              <Button color="primary" disabled={saving || !commentText.trim()}>
                Post comment
              </Button>
            </form>
          </section>
          {(isAdmin || canManageStatus) && (
            <section className="ticket-panel ticket-admin-controls">
              <div className="ticket-admin-controls-heading">
                <div className="ticket-panel-title">Admin controls</div>
                <p>Keep the ticket state and urgency up to date.</p>
              </div>
              {canManageStatus && (
                <label className="ticket-admin-control-field">
                  <span>Ticket status</span>
                  <Input
                    type="select"
                    value={ticketStatusValue(selectedTicket)}
                    onChange={changeStatus}
                    disabled={saving}
                  >
                    {STATUS_OPTIONS.map(option => (
                      <option value={option.value} key={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </label>
              )}
              <label className="ticket-admin-control-field">
                <span>Priority</span>
                <Input
                  type="select"
                  value={first(selectedTicket.priority, "Medium")}
                  onChange={event =>
                    updateTicket("priority", event.target.value)
                  }
                  disabled={saving}
                >
                  {PRIORITIES.map(option => (
                    <option key={option}>{option}</option>
                  ))}
                </Input>
              </label>
            </section>
          )}
        </div>
        <Modal
          isOpen={Boolean(previewAttachment)}
          centered
          toggle={() => setPreviewAttachment(null)}
          className="ticket-image-preview-modal"
        >
          <ModalHeader toggle={() => setPreviewAttachment(null)}>
            {previewAttachment?.name || "Image preview"}
          </ModalHeader>
          <ModalBody>
            <img
              className="ticket-image-preview"
              src={previewAttachment?.url}
              alt={previewAttachment?.name || "Attachment preview"}
            />
          </ModalBody>
        </Modal>
      </div>
    )

  return (
    <div className="ticket-page">
      <div className="ticket-hero">
        <div className="ticket-hero-icon">
          <FaTicketAlt />
        </div>
        <div>
          <span className="ticket-kicker">
            {isAdmin ? "ADMIN WORKSPACE" : "EMPLOYEE SUPPORT"}
          </span>
          <h1>Ticket Management</h1>
          <p>
            Track requests, collaborate with support, and keep every resolution
            visible.
          </p>
        </div>
        <div className="ticket-company">
          {isAdmin && (
            <>
              <label>Selected company</label>
              <CompanySelector
                selectedCompanyId={companyId}
                onCompanyChange={company =>
                  setCompanyId(company?.company_id || "")
                }
              />
            </>
          )}
        </div>
        <div className="ticket-hero-actions">
          {!isAdmin && (
            <Button
              color="primary"
              className="ticket-raise"
              onClick={() => setShowCreate(true)}
            >
              <FaPlus /> Raise Ticket
            </Button>
          )}
          <Button
            color="light"
            className="ticket-refresh"
            onClick={fetchTickets}
            disabled={loading}
            title="Reload tickets"
            aria-label="Reload tickets"
          >
            <FaRedo className={loading ? "is-spinning" : ""} />
          </Button>
        </div>
      </div>
      <div className="ticket-status-strip">
        <div className="ticket-count all">
          <span>All tickets</span>
          <strong>{counts.All}</strong>
        </div>
        {STATUSES.filter(status => status !== "Waiting on Requester").map(status => (
          <div
            className={`ticket-count ${normalize(status).replace(" ", "-")}`}
            key={status}
          >
            <span>{status}</span>
            <strong>{counts[status]}</strong>
          </div>
        ))}
      </div>
      <div className="ticket-filter-bar ticket-search-filter-bar">
        <div className="ticket-filter-heading">
          <FaFilter />
          <strong>Filter tickets</strong>
        </div>
        <Input
          type="date"
          aria-label="From date"
          title="From date"
          value={filters.date_from}
          onChange={event =>
            updateTicketFilter("date_from", event.target.value)
          }
        />
        <Input
          type="date"
          aria-label="To date"
          title="To date"
          value={filters.date_to}
          onChange={event => updateTicketFilter("date_to", event.target.value)}
        />
        <Input
          type="select"
          aria-label="Priority"
          value={filters.priority}
          onChange={event => updateTicketFilter("priority", event.target.value)}
        >
          <option value="">All</option>
          {PRIORITIES.map(option => (
            <option key={option} value={option.toLowerCase()}>
              {option}
            </option>
          ))}
        </Input>
        <Input
          type="select"
          aria-label="Status"
          value={filters.status}
          onChange={event => updateTicketFilter("status", event.target.value)}
        >
          <option value="">All</option>
          {STATUS_OPTIONS.filter(
            option => option.value !== "waiting_on_requester"
          ).map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Input>
        <Button color="light" onClick={clearTicketFilters} disabled={loading}>
          Clear Filters
        </Button>
      </div>
      <div className="ticket-list-panel">
        <div className="ticket-list-heading">
          <div>
            <h2>{isAdmin ? "All tickets" : "My tickets"}</h2>
            <span>
              {ticketCount} {ticketCount === 1 ? "ticket" : "tickets"}
            </span>
          </div>
          <FaSearch className="ticket-heading-icon" />
        </div>
        {loading ? (
          <div className="ticket-loading">Loading tickets...</div>
        ) : visibleTickets.length ? (
          <div className="ticket-table-wrap">
            <table className="ticket-table">
              <thead>
                <tr>
                  {[
                    ["Ticket number", "ticket_number"],
                    ["Module", "module"],
                    ["Subject", "subject"],
                    ["Description", "description"],
                    ["Priority", "priority"],
                    ["Status", "status"],
                    ["Resolution feedback", "resolution_feedback"],
                    ["Created By", "created_by"],
                    ["Attachments", "attachments"],
                    ["Comments", "comments"],
                    ["Created date", "created_at"],
                    ["Last updated", "updated_at"],
                  ].map(([title, key]) => (
                    <th key={key} title={`Sort by ${title}`}>
                      <button
                        type="button"
                        className="ticket-sort-button"
                        onClick={() => sortTickets(key)}
                        title={`Sort by ${title}`}
                      >
                        {title} {sortIcon(key)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedTickets.map(ticket => (
                  <tr
                    key={idOf(ticket)}
                    onClick={() => openTicket(ticket)}
                  >
                    <td>
                      <button type="button" className="ticket-number-link">
                        {ticketNumber(ticket)}
                      </button>
                    </td>
                    <td>
                      <span className="ticket-module-pill">
                        {ticket.module || "Other"}
                      </span>
                    </td>
                    <td>
                      <strong>{ticket.subject || "Untitled ticket"}</strong>
                    </td>
                    <td>
                      <span
                        className="ticket-description-cell"
                        title={ticket.description || "No description provided"}
                      >
                        {ticket.description || "No description provided"}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`ticket-priority priority-${normalize(
                          ticket.priority
                        ).replace(" ", "-")}`}
                      >
                        {first(ticket.priority, "Medium")}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`ticket-status status-${normalize(
                          ticket.status
                        ).replace(" ", "-")}`}
                      >
                        <FaCheckCircle />
                        {ticketStatusLabel(ticket.status)}
                      </span>
                    </td>
                    <td>
                      {ticketFeedbackStatus(ticket) ? (
                        <span
                          className={`ticket-feedback-badge feedback-${ticketFeedbackStatus(
                            ticket
                          )}`}
                        >
                          {resolutionLabel(ticketFeedbackStatus(ticket))}
                        </span>
                      ) : (
                        <span className="ticket-muted-cell">-</span>
                      )}
                    </td>
                    <td>
                      <strong className="ticket-created-by">
                        {ticketCreatedBy(ticket)}
                      </strong>
                    </td>
                    <td>
                      <span className="ticket-count-cell">
                        <FaPaperclip />{" "}
                        {ticketAttachments(ticket).length ||
                          first(ticket.attachment_count, 0)}
                      </span>
                    </td>
                    <td>
                      <span className="ticket-count-cell">
                        <FaComments /> {ticketCommentCount(ticket)}
                      </span>
                    </td>
                    <td>
                      {formatDate(
                        first(ticket.created_at, ticket.created_date)
                      )}
                    </td>
                    <td>
                      {formatDate(
                        first(ticket.updated_at, ticket.last_updated)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="ticket-empty">
            <div className="ticket-empty-icon">
              <FaTicketAlt />
            </div>
            <span className="ticket-empty-kicker">TICKET WORKSPACE</span>
            <h3>No tickets found</h3>
            <p>
              {isAdmin
                ? "Try changing the filters or create a ticket from the employee view."
                : "Raise a ticket when you need help from the ATS team."}
            </p>
            {!isAdmin && (
              <Button color="primary" onClick={() => setShowCreate(true)}>
                <FaPlus /> Raise Ticket
              </Button>
            )}
          </div>
        )}
      </div>
      <Modal
        isOpen={showCreate}
        centered
        toggle={closeCreateModal}
        className="ticket-create-modal"
      >
        <ModalHeader toggle={closeCreateModal}>
          Create a support ticket
        </ModalHeader>
        <form onSubmit={createTicket}>
          <ModalBody>
            <label>
              Module *
              <Input
                type="select"
                value={form.module}
                onChange={event =>
                  setForm({ ...form, module: event.target.value })
                }
              >
                <option value="">Select module</option>
                {MODULES.map(option => (
                  <option key={option}>{option}</option>
                ))}
              </Input>
            </label>
            <label>
              Subject *
              <Input
                value={form.subject}
                onChange={event =>
                  setForm({ ...form, subject: event.target.value })
                }
                placeholder="Briefly describe the issue"
              />
            </label>
            <label>
              Description *
              <Input
                type="textarea"
                rows="5"
                value={form.description}
                onChange={event =>
                  setForm({ ...form, description: event.target.value })
                }
                placeholder="Include useful details, steps, or error messages"
              />
            </label>
            <label className="ticket-create-priority-field">
              Priority
              <Input
                type="select"
                value={form.priority}
                onChange={event =>
                  setForm({ ...form, priority: event.target.value })
                }
              >
                {PRIORITIES.map(option => (
                  <option key={option}>{option}</option>
                ))}
              </Input>
            </label>
            <div
              className={`ticket-upload-area ${
                dragActive ? "is-dragging" : ""
              }`}
              onDragEnter={event => {
                event.preventDefault()
                setDragActive(true)
              }}
              onDragOver={event => event.preventDefault()}
              onDragLeave={event => {
                event.preventDefault()
                setDragActive(false)
              }}
              onDrop={event => {
                event.preventDefault()
                setDragActive(false)
                addFiles(event.dataTransfer.files)
              }}
            >
              <FaCloudUploadAlt />
              <strong>Drag and drop files here</strong>
              <span>or</span>
              <label className="ticket-browse-button">
                Browse Files
                <input
                  type="file"
                  multiple
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx"
                  onChange={event => {
                    addFiles(event.target.files)
                    event.target.value = ""
                  }}
                />
              </label>
              <small>
                PNG, JPG, JPEG, PDF, DOC, DOCX, XLS, XLSX · Max 10 MB per file
              </small>
            </div>
            {selectedFiles.length > 0 && (
              <div className="ticket-selected-files">
                <div className="ticket-selected-files-heading">
                  <strong>Selected files</strong>
                  <span>
                    {selectedFiles.length} file
                    {selectedFiles.length === 1 ? "" : "s"}
                  </span>
                </div>
                {selectedFiles.map(item => (
                  <div className="ticket-selected-file" key={item.id}>
                    {item.preview ? (
                      <img src={item.preview} alt={item.file.name} />
                    ) : (
                      <FaFileAlt className="ticket-selected-file-icon" />
                    )}
                    <div>
                      <strong>{item.file.name}</strong>
                      <small>
                        {fileSize(item.file.size)} ·{" "}
                        {fileExtension(item.file.name).toUpperCase()}
                      </small>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(item.id)}
                      title="Remove file"
                    >
                      <FaTimes />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button type="button" color="light" onClick={closeCreateModal}>
              Cancel
            </Button>
            <Button type="submit" color="primary" disabled={saving}>
              {saving ? "Submitting..." : "Submit ticket"}
            </Button>
          </ModalFooter>
        </form>
      </Modal>
    </div>
  )
}

export default TicketManagement
